import {
  CollisionDetection,
  rectIntersection,
  type DropAnimation,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "../ui/card";
import { Dumbbell, Plus } from "lucide-react";
import { Tables } from "@/lib/supabase/supabaseTypes";
import { ExerciseCollection } from "./workoutsColumns";
import { createWorkoutExercise } from "./workoutEditorUtils";

export type ExerciseDragData = {
  type: "exercise";
  exerciseId: string;
  exerciseName: string;
  sourceGroupIndex: number;
  sourceExerciseIndex: number;
};

export type GroupDragData = {
  type: "group";
  groupId: string;
  exerciseCount: number;
  sourceGroupIndex: number;
};

export type LibraryExerciseDragData = {
  type: "library-exercise";
  exerciseId: string;
  exerciseName: string;
  exercise: Tables<"exercises">;
};

export type WorkoutDragData =
  | ExerciseDragData
  | GroupDragData
  | LibraryExerciseDragData;

export type GroupInsertDropZoneData = {
  type: "group-insert";
  index: number;
};

export type SupersetInsertDropZoneData = {
  type: "superset-insert";
  groupIndex: number;
  position: number;
};

export type WorkoutDropZoneData =
  | GroupInsertDropZoneData
  | SupersetInsertDropZoneData;

export type RecentDropState =
  | {
      type: "exercise";
      exerciseId: string;
    }
  | {
      type: "group";
      groupId: string;
    };

export const DROP_ANIMATION_DURATION_MS = 400;

export const WORKOUT_DROP_ANIMATION: DropAnimation = {
  duration: DROP_ANIMATION_DURATION_MS,
  easing: "cubic-bezier(0.22, 1, 0.36, 1)",
  keyframes: ({ transform }) => [
    {
      offset: 0,
      opacity: 1,
      transform: CSS.Transform.toString(transform.initial),
    },
    {
      offset: 0.8,
      opacity: 1,
      transform: CSS.Transform.toString(transform.final),
    },
    {
      offset: 1,
      opacity: 0,
      transform: CSS.Transform.toString(transform.final),
    },
  ],
  sideEffects: ({ active }) => {
    active.node.style.opacity = "0";

    const fadeInAnimation = active.node.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 0, offset: 0.8 },
        { opacity: 1, offset: 1 },
      ],
      {
        duration: DROP_ANIMATION_DURATION_MS,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "both",
      },
    );

    return () => {
      fadeInAnimation.cancel();
      active.node.style.removeProperty("opacity");
    };
  },
};

const cloneExerciseCollection = (exerciseCollection: ExerciseCollection[]) =>
  exerciseCollection.map((group) => [...group]);

const normalizeExerciseCollection = (
  exerciseCollection: ExerciseCollection[],
) =>
  exerciseCollection
    .filter((group) => group.length > 0)
    .map((group, groupIndex) =>
      group.map((exercise, exerciseIndex) => ({
        ...exercise,
        ex_order: groupIndex,
        sub_order: exerciseIndex,
      })),
    );

export const moveGroupToIndex = (
  exerciseCollection: ExerciseCollection[],
  dragData: GroupDragData,
  dropZone: GroupInsertDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const [group] = groups.splice(dragData.sourceGroupIndex, 1);
  if (!group) {
    return null;
  }

  const adjustedIndex =
    dragData.sourceGroupIndex < dropZone.index
      ? dropZone.index - 1
      : dropZone.index;

  if (adjustedIndex === dragData.sourceGroupIndex) {
    return null;
  }

  groups.splice(adjustedIndex, 0, group);

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "group" as const,
      groupId: dragData.groupId,
    },
  };
};

export const moveExerciseToGroupIndex = (
  exerciseCollection: ExerciseCollection[],
  dragData: ExerciseDragData,
  dropZone: GroupInsertDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const sourceGroupWasSingle = groups[dragData.sourceGroupIndex]?.length === 1;

  if (
    sourceGroupWasSingle &&
    (dropZone.index === dragData.sourceGroupIndex ||
      dropZone.index === dragData.sourceGroupIndex + 1)
  ) {
    return null;
  }

  const [exercise] = groups[dragData.sourceGroupIndex].splice(
    dragData.sourceExerciseIndex,
    1,
  );

  if (!exercise) {
    return null;
  }

  if (groups[dragData.sourceGroupIndex].length === 0) {
    groups.splice(dragData.sourceGroupIndex, 1);
  }

  const adjustedIndex =
    sourceGroupWasSingle && dragData.sourceGroupIndex < dropZone.index
      ? dropZone.index - 1
      : dropZone.index;

  groups.splice(adjustedIndex, 0, [exercise]);

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "exercise" as const,
      exerciseId: dragData.exerciseId,
    },
  };
};

export const moveExerciseToSupersetPosition = (
  exerciseCollection: ExerciseCollection[],
  dragData: ExerciseDragData,
  dropZone: SupersetInsertDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const sourceGroupWasSingle = groups[dragData.sourceGroupIndex]?.length === 1;
  const sourceGroupIndex = dragData.sourceGroupIndex;
  const targetGroupIndex =
    sourceGroupWasSingle && sourceGroupIndex < dropZone.groupIndex
      ? dropZone.groupIndex - 1
      : dropZone.groupIndex;

  const [exercise] = groups[sourceGroupIndex].splice(
    dragData.sourceExerciseIndex,
    1,
  );

  if (!exercise) {
    return null;
  }

  if (groups[sourceGroupIndex].length === 0) {
    groups.splice(sourceGroupIndex, 1);
  }

  const targetGroup = groups[targetGroupIndex];
  if (!targetGroup) {
    return null;
  }

  const adjustedPosition =
    !sourceGroupWasSingle &&
    sourceGroupIndex === dropZone.groupIndex &&
    dragData.sourceExerciseIndex < dropZone.position
      ? dropZone.position - 1
      : dropZone.position;

  if (
    !sourceGroupWasSingle &&
    sourceGroupIndex === dropZone.groupIndex &&
    adjustedPosition === dragData.sourceExerciseIndex
  ) {
    return null;
  }

  targetGroup.splice(adjustedPosition, 0, exercise);

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "exercise" as const,
      exerciseId: dragData.exerciseId,
    },
  };
};

export const insertLibraryExerciseAtDropZone = (
  exerciseCollection: ExerciseCollection[],
  dragData: LibraryExerciseDragData,
  dropZone: WorkoutDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const newExercise = createWorkoutExercise({
    exercise: dragData.exercise,
    exOrder: 0,
    subOrder: 0,
  });

  if (dropZone.type === "group-insert") {
    groups.splice(dropZone.index, 0, [newExercise]);
  } else {
    const targetGroup = groups[dropZone.groupIndex];
    if (!targetGroup) {
      return null;
    }
    targetGroup.splice(dropZone.position, 0, newExercise);
  }

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "exercise" as const,
      exerciseId: newExercise.sortableId,
    },
  };
};

export const getGroupDragId = (group: ExerciseCollection) =>
  `group-${group.map(({ sortableId }) => sortableId).join("-")}`;

export const getGroupKey = (group: ExerciseCollection) =>
  `group-key-${group.map(({ sortableId }) => sortableId).join("-")}`;

export const isWorkoutDragData = (value: unknown): value is WorkoutDragData =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  (value.type === "exercise" ||
    value.type === "group" ||
    value.type === "library-exercise");

export const isWorkoutDropZoneData = (
  value: unknown,
): value is WorkoutDropZoneData =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  (value.type === "group-insert" || value.type === "superset-insert");

const isPointWithinRect = (
  point: { x: number; y: number },
  rect: { top: number; right: number; bottom: number; left: number },
) =>
  rect.top <= point.y &&
  point.y <= rect.bottom &&
  rect.left <= point.x &&
  point.x <= rect.right;

const getDistanceToRectCenter = (
  point: { x: number; y: number },
  rect: { top: number; left: number; width: number; height: number },
) => {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  return Math.hypot(point.x - centerX, point.y - centerY);
};

export const workoutCollisionDetection: CollisionDetection = (args) => {
  const { pointerCoordinates, droppableContainers, droppableRects } = args;

  if (pointerCoordinates) {
    const pointerCollisions = droppableContainers
      .map((droppableContainer) => {
        const rect = droppableRects.get(droppableContainer.id);

        if (!rect || !isPointWithinRect(pointerCoordinates, rect)) {
          return null;
        }

        return {
          id: droppableContainer.id,
          data: {
            droppableContainer,
            value: getDistanceToRectCenter(pointerCoordinates, rect),
          },
        };
      })
      .filter((collision) => collision !== null)
      .sort((a, b) => a.data.value - b.data.value);

    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
  }

  return rectIntersection(args);
};

type WorkoutDragOverlayContentProps = {
  activeDrag: WorkoutDragData | null;
};

export const WorkoutDragOverlayContent = ({
  activeDrag,
}: WorkoutDragOverlayContentProps) => {
  if (!activeDrag) {
    return null;
  }

  if (activeDrag.type === "group") {
    return (
      <Card className="w-[280px] border-foreground/10 bg-background/95 px-4 py-3 shadow-xl backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
            Superset
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">Superset</p>
            <p className="text-xs text-muted-foreground">
              {activeDrag.exerciseCount}{" "}
              {activeDrag.exerciseCount === 1 ? "exercise" : "exercises"}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const isLibraryExercise = activeDrag.type === "library-exercise";

  return (
    <Card className="w-[260px] border-foreground/10 bg-background/95 px-4 py-3 shadow-xl backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          {isLibraryExercise ? (
            <Plus className="h-4 w-4" />
          ) : (
            <Dumbbell className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {isLibraryExercise ? "New Exercise" : "Exercise"}
          </p>
          <p className="truncate text-sm font-medium">
            {activeDrag.exerciseName}
          </p>
        </div>
      </div>
    </Card>
  );
};
